import React, { useCallback, useEffect, useState } from 'react';
import '@brands/Dashboard/MultiStage/multiStage.scss';
import { caseTypeLabel, isClinicalCaseType, TypeIcon } from '@brands/Dashboard/MultiStage/multiStageUi';
import CloseIcon from '@mui/icons-material/Close';

import {
  deletePendingLinkedCase,
  getCaseJourneys,
  getFollowUpOptions,
  recordFollowUpDecision,
  savePendingLinkedCase,
} from '@brands/services/cases/multiStage';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { ICase } from '@brands/services/cases/types/ICase';
import { displayErrorDetails } from '@brands/Utils/displayError';

export const MAX_JOURNEY_CASES = 7;

const CASE_TYPE_DESC: Record<string, string> = {
  video_call_scheduled: 'Live virtual visit with a dentist',
  phone_call_scheduled: 'Phone follow-up',
  one_report: 'Async photo-based review',
  second_opinion: 'Async review by another dentist',
  care_coordination: 'Hand off to a local dental office',
  oral_health_coaching: 'Ongoing guidance from a coach',
  clinical_enablement: 'Custom appliance or device',
};

const FOLLOW_UP_CARD_LABELS: Record<string, string> = {
  video_call_scheduled: 'Video consultation',
  video_call_instant: 'Video consultation',
  phone_call_scheduled: 'Phone call',
  second_opinion: 'Second opinion',
  one_report: 'Photo review',
  care_coordination: 'Care Coordination / Referral to in-person care',
  oral_health_coaching: 'Oral Health Coaching',
};

const label = (t: string): string => FOLLOW_UP_CARD_LABELS[t] || caseTypeLabel(t);

const NONE = 'none';
const FOLLOW_UP_DISPLAY_ORDER = [
  'oral_health_coaching',
  'care_coordination',
  'video_call_scheduled',
  'video_call_instant',
  'phone_call_scheduled',
  'second_opinion',
  'one_report',
  'clinical_enablement',
];

const optionRank = (type: string): number => {
  const index = FOLLOW_UP_DISPLAY_ORDER.indexOf(type);
  return index === -1 ? FOLLOW_UP_DISPLAY_ORDER.length : index;
};

// "Case details" panel options: the referral specialty list + urgency scale
// taken from the design.
const TYPE_OPTIONS = ['Dentist', 'Periodontist', 'Endodontist', 'Orthodontist', 'Oral Surgeon'];
const URGENCY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'];

type Props = {
  thisCase: ICase;
  setCase: React.Dispatch<React.SetStateAction<any>>;
  disabled?: boolean;
  onClose?: () => void;
};

// DO-3881 / DO-3898: expand the consultation "Add follow-up care" into a
// dynamic option-card picker driven by the backend matrix; auto-creates a
// linked case in the journey (assign-to-me optional), capped at 7.
const AddFollowUpCareSection = ({ thisCase, setCase, disabled = false, onClose }: Props): JSX.Element => {
  const [options, setOptions] = useState<string[]>([]);
  const [linkedCount, setLinkedCount] = useState<number>(1);
  const [journeyPaused, setJourneyPaused] = useState<boolean>(false);
  // Seed from any selection already saved on the case so re-opening the
  // picker edits the existing (single) pending selection rather than adding
  // a second one.
  const hasPending = Boolean(thisCase.pending_linked_case_type);
  const [selected, setSelected] = useState<string>(thisCase.pending_linked_case_type || '');
  const [assignToMe, setAssignToMe] = useState<boolean>(Boolean(thisCase.pending_linked_case_assign_to_me));
  const [detailType, setDetailType] = useState<string>(thisCase.pending_follow_up_type || '');
  const [urgency, setUrgency] = useState<string>(thisCase.pending_follow_up_urgency || '');
  const [description, setDescription] = useState<string>(thisCase.pending_follow_up_description || '');
  const [saving, setSaving] = useState<boolean>(false);

  const resetFields = (): void => {
    setSelected('');
    setAssignToMe(false);
    setDetailType('');
    setUrgency('');
    setDescription('');
  };

  const load = useCallback(async (): Promise<void> => {
    // Independent reads — run concurrently; each is best-effort (ignored on error).
    const [optsRes, journeysRes] = await Promise.allSettled([
      getFollowUpOptions(Number(thisCase.id)),
      getCaseJourneys(Number(thisCase.patient_id)),
    ]);
    if (optsRes.status === 'fulfilled') {
      setOptions(optsRes.value.allowed_case_types);
    }
    if (journeysRes.status === 'fulfilled') {
      const journey = journeysRes.value.multi_stage.find((g) =>
        g.cases.some((c) => Number(c.id) === Number(thisCase.id)),
      );
      setLinkedCount(journey ? journey.cases.length : 1);
      setJourneyPaused(journey?.status === 'paused');
    }
  }, [thisCase.id, thisCase.patient_id]);

  useEffect(() => {
    load();
  }, [load]);

  const atLimit = linkedCount >= MAX_JOURNEY_CASES;
  // A paused journey shouldn't gain new follow-up cases until it's resumed, but
  // "no additional case needed" closes the journey out rather than growing it,
  // so it stays available while paused.
  const editingDisabled = disabled || journeyPaused;
  const isOptionDisabled = (c: string): boolean => (c === NONE ? disabled : editingDisabled);

  // Shared tail for the save/remove actions: pull the fresh case, hand it to
  // the parent, clear local inputs, then close (unmounts) or reload lists.
  const refreshAndClose = async (): Promise<void> => {
    const refreshed = await getOneCase(Number(thisCase.id));
    setCase(refreshed);
    resetFields();
    if (onClose) {
      onClose();
    } else {
      await load();
    }
  };

  // "Assign this case to me" only applies to clinical follow-up cases. Non-clinical
  // types (care coordination, oral health coaching, …) are worked by coordinators/
  // coaches, so the option is disabled and never sent.
  const assignToMeAllowed = selected !== NONE && isClinicalCaseType(selected);
  const assignDisabled = editingDisabled || !assignToMeAllowed;
  const toggleAssign = (): void => {
    if (!assignDisabled) setAssignToMe((v) => !v);
  };

  const handleAdd = async (): Promise<void> => {
    if (!selected) {
      return;
    }
    setSaving(true);
    try {
      if (selected === NONE) {
        // Choosing "none needed" must drop any previously-saved selection,
        // otherwise it would still be materialized on submit.
        if (hasPending) {
          await deletePendingLinkedCase(Number(thisCase.id));
        }
        await recordFollowUpDecision(Number(thisCase.id), true);
      } else {
        await savePendingLinkedCase(Number(thisCase.id), {
          case_type: selected,
          assign_to_me: assignToMeAllowed && assignToMe,
          type: detailType || undefined,
          urgency: urgency || undefined,
          description: description.trim() || undefined,
        });
      }
      await refreshAndClose();
    } catch (e) {
      displayErrorDetails(e as any);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (): Promise<void> => {
    setSaving(true);
    try {
      await deletePendingLinkedCase(Number(thisCase.id));
      await refreshAndClose();
    } catch (e) {
      displayErrorDetails(e as any);
    } finally {
      setSaving(false);
    }
  };

  const sortedOptions = Array.from(new Set(options)).sort((a, b) => {
    const rankDiff = optionRank(a) - optionRank(b);
    return rankDiff || a.localeCompare(b);
  });
  const choices = [...sortedOptions, NONE];
  const pick = (c: string): void => {
    if (!isOptionDisabled(c)) setSelected(c);
  };

  // eslint-disable-next-line no-nested-ternary
  const primaryLabel = saving ? 'Saving…' : hasPending ? 'Update follow-up care' : 'Add follow-up care';
  const title = hasPending ? 'Edit follow-up care' : 'Add follow-up care';

  return (
    <div className="ms-scope ms-follow-up-picker">
      {onClose && (
        <div className="ms-follow-up-picker__header">
          <h2 className="ms-follow-up-picker__modal-title">{title}</h2>
          <button
            type="button"
            className="ms-follow-up-picker__close"
            aria-label="Close follow-up care modal"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>
      )}

      <div className="ms-card ms-follow-up-picker__card">
        <h3 className="ms-title ms-follow-up-picker__question">
          What follow-up does this patient need?<span className="ms-req">*</span>
        </h3>

        {journeyPaused && (
          <p className="ms-error" style={{ marginBottom: 8 }}>
            This multi-stage case is paused and a new case can’t be added. You can still select “No additional
            case needed”.
          </p>
        )}

        {!journeyPaused && atLimit && (
          <p className="ms-error" style={{ marginBottom: 8 }}>
            You&apos;ve reached the 7-case maximum for this journey.
          </p>
        )}

        <div className="ms-opt-grid">
          {choices.map((c) => {
            const on = selected === c;
            const isNone = c === NONE;
            const optionDisabled = isOptionDisabled(c);
            return (
              <div
                key={c}
                className={`ms-opt-card${on ? ' is-on' : ''}${isNone ? ' is-none' : ''}${
                  optionDisabled ? ' is-disabled' : ''
                }`}
                onClick={() => pick(c)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && pick(c)}
              >
                <span className="ms-opt-card__radio" />
                {!isNone && (
                  <span className="ms-opt-card__icon">
                    <TypeIcon type={c} />
                  </span>
                )}
                <span className="ms-opt-card__title">{isNone ? 'No additional case needed' : label(c)}</span>
                {!isNone && <span className="ms-opt-card__desc">{CASE_TYPE_DESC[c] || ''}</span>}
              </div>
            );
          })}
        </div>

        {selected && (
          <>
            {selected !== NONE && (
              <>
                <div className="ms-assign-label">Case details</div>
                <div className="ms-case-details-panel">
                  <div className="ms-field-row">
                    <label className="ms-field-label">
                      Type<span className="ms-req">*</span>
                    </label>
                    <select
                      className="ms-field-select"
                      value={detailType}
                      disabled={editingDisabled}
                      onChange={(e) => setDetailType(e.target.value)}
                    >
                      <option value="" disabled>
                        Select a type
                      </option>
                      {TYPE_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ms-field-row">
                    <label className="ms-field-label">
                      Urgency<span className="ms-req">*</span>
                    </label>
                    <select
                      className="ms-field-select"
                      value={urgency}
                      disabled={editingDisabled}
                      onChange={(e) => setUrgency(e.target.value)}
                    >
                      <option value="" disabled>
                        Select urgency
                      </option>
                      {URGENCY_OPTIONS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ms-field-row">
                    <label className="ms-field-label">Description (optional)</label>
                    <textarea
                      className="ms-field-textarea"
                      value={description}
                      disabled={editingDisabled}
                      placeholder="Add any relevant context"
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                </div>

                <div className="ms-assign-label">Assignment</div>
                <div
                  className={`ms-assignment-card${assignToMe && assignToMeAllowed ? ' is-on' : ''}${
                    assignDisabled ? ' is-disabled' : ''
                  }`}
                  onClick={toggleAssign}
                  role="button"
                  aria-disabled={assignDisabled}
                  tabIndex={assignDisabled ? -1 : 0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleAssign()}
                >
                  <span className="ms-assignment-card__cb">✓</span>
                  <span className="ms-assignment-card__label">Assign this case to me</span>
                </div>
              </>
            )}

            <button
              type="button"
              className="ms-primary-btn"
              disabled={
                isOptionDisabled(selected) || saving || (selected !== NONE && (atLimit || !detailType || !urgency))
              }
              onClick={handleAdd}
            >
              {primaryLabel}
            </button>
            {hasPending && (
              <button
                type="button"
                className="ms-text-btn"
                disabled={editingDisabled || saving}
                onClick={handleRemove}
              >
                Remove follow-up care
              </button>
            )}
            {onClose && (
              <button type="button" className="ms-text-btn" disabled={saving} onClick={onClose}>
                Cancel
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AddFollowUpCareSection;
